'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  verifyPatientOnboardingToken,
  submitPatientOnboarding,
} from '@/client/sdk.gen';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle } from 'lucide-react';
import { PatientOnboardingAnswers } from '@/client/types.gen';

// Schema for the form
const patientOnboardingSchema = z.object({
  // Personal information
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  gender: z.enum(['male', 'female', 'prefer_not_to_say'] as const, {
    required_error: 'Please select a gender',
  }),
  date_of_birth: z.string().min(1, 'Date of birth is required'),

  // Preferences
  preferred_language: z.string().optional(),
  preferred_communication: z
    .enum(['Email', 'Phone', 'SMS'] as const)
    .optional(),

  // Medical history
  medical_history: z.string().optional(),
  family_medical_history: z.string().optional(),
  social_history: z.string().optional(),
  occupation: z.string().optional(),
  smoking_status: z
    .enum(['Never', 'Former', 'Current', 'Unknown'] as const)
    .optional(),
  alcohol_consumption: z.string().optional(),
  exercise_habits: z.string().optional(),
  diet_notes: z.string().optional(),
});

type PatientOnboardingFormValues = z.infer<typeof patientOnboardingSchema>;

export default function PatientOnboardPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<PatientOnboardingFormValues>({
    resolver: zodResolver(patientOnboardingSchema),
    defaultValues: {
      name: '',
      email: '',
      gender: 'prefer_not_to_say',
      date_of_birth: '',
      preferred_language: '',
      preferred_communication: undefined,
      medical_history: '',
      family_medical_history: '',
      social_history: '',
      occupation: '',
      smoking_status: undefined,
      alcohol_consumption: '',
      exercise_habits: '',
      diet_notes: '',
    },
  });

  // Verify token and get patient details
  useEffect(() => {
    if (!token) {
      setError('Missing token parameter. Please use the link sent to you.');
      setIsLoading(false);
      return;
    }

    verifyPatientOnboardingToken({
      query: { token },
    })
      .then((response) => {
        if (response.data) {
          // Pre-fill form with existing data
          const formValues: Partial<PatientOnboardingFormValues> = {
            name: response.data.name,
            email: response.data.email || '',
            gender: response.data.gender,
            date_of_birth: response.data.date_of_birth,
            preferred_language: response.data.preferred_language || '',
            preferred_communication: response.data.preferred_communication as
              | 'Email'
              | 'Phone'
              | 'SMS'
              | undefined,
          };

          // Pre-fill profile data if available
          if (response.data.profile) {
            formValues.medical_history =
              response.data.profile.medical_history || '';
            formValues.family_medical_history =
              response.data.profile.family_medical_history || '';
            formValues.social_history =
              response.data.profile.social_history || '';
            formValues.occupation = response.data.profile.occupation || '';
            formValues.smoking_status = response.data.profile.smoking_status as
              | 'Never'
              | 'Former'
              | 'Current'
              | 'Unknown'
              | undefined;
            formValues.alcohol_consumption =
              response.data.profile.alcohol_consumption || '';
            formValues.exercise_habits =
              response.data.profile.exercise_habits || '';
            formValues.diet_notes = response.data.profile.diet_notes || '';
          }

          form.reset(formValues);
        }
      })
      .catch((error) => {
        console.error('Error verifying token:', error);
        setError('Invalid or expired token. Please request a new link.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, form]);

  const onSubmit = async (data: PatientOnboardingFormValues) => {
    if (!token) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Construct the submission data
      const submissionData: PatientOnboardingAnswers = {
        medical_history: data.medical_history || null,
        family_medical_history: data.family_medical_history || null,
        social_history: data.social_history || null,
        occupation: data.occupation || null,
        smoking_status: data.smoking_status || null,
        alcohol_consumption: data.alcohol_consumption || null,
        exercise_habits: data.exercise_habits || null,
        diet_notes: data.diet_notes || null,
        preferred_communication: data.preferred_communication || 'Email',
      };

      // Submit the data
      await submitPatientOnboarding({
        body: submissionData,
        query: { token },
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(
        'There was an error submitting your information. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading your information...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 md:p-8 max-w-3xl mx-auto">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="p-4 sm:p-6 md:p-8 max-w-3xl mx-auto flex flex-col items-center justify-center min-h-[50vh]">
        <div className="text-center space-y-4">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
          <h2 className="text-2xl font-bold">
            Information Updated Successfully
          </h2>
          <p className="text-muted-foreground">
            Thank you for updating your medical information. Your healthcare
            provider now has the information they need.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Patient Information Form</h1>
      <p className="mb-6 text-muted-foreground">
        Please complete this form to provide your healthcare provider with
        important information about your health. Your privacy is important to
        us, and this information will be kept confidential.
      </p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="bg-card p-4 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="prefer_not_to_say">
                          Prefer not to say
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="date_of_birth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date of Birth</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4">Preferences</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="preferred_language"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preferred Language</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="preferred_communication"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preferred Communication Method</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Email">Email</SelectItem>
                        <SelectItem value="Phone">Phone</SelectItem>
                        <SelectItem value="SMS">SMS</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4">Medical History</h2>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="medical_history"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Medical History</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Please list any significant medical conditions, surgeries, or hospitalizations"
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="family_medical_history"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Family Medical History</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Please list any significant medical conditions in your family"
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold mb-4">
              Lifestyle Information
            </h2>
            <div className="grid grid-cols-1 gap-4">
              <FormField
                control={form.control}
                name="occupation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Occupation</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="smoking_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Smoking Status</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Never">Never Smoked</SelectItem>
                        <SelectItem value="Former">Former Smoker</SelectItem>
                        <SelectItem value="Current">Current Smoker</SelectItem>
                        <SelectItem value="Unknown">
                          Prefer not to say
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="alcohol_consumption"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Alcohol Consumption</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Occasional, Weekly, None"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="exercise_habits"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Exercise Habits</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., 3 times a week, Daily walks"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="diet_notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dietary Information</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Any dietary restrictions or special diets"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="social_history"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Social History</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Any additional lifestyle information you'd like to share"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Submit Information
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
